import { RubyGemMark } from '../components/RubyGemMark';
import { RubyLoader } from '../components/RubyLoader';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Tag, Plus, RefreshCw, Search, ChevronRight, Copy, Globe,
  Edit2, Trash2, Star, Check, X, AlertCircle, AlertTriangle,
} from 'lucide-react';
import type { DealTag } from '@platform/shared-types';
import SearchableDropdown from '../components/SearchableDropdown';
import { money } from '../components/finance/financeFormat';
import { api } from '../services/apiService';
import { billingApi, type BillingSkuRow } from '../services/billingApi';
import { hardwareApi, type DeviceBundleRow, type DeviceModelRow } from '../services/hardwareApi';
import { dealsApi, type ActiveDealVersionSummary } from '../services/dealsApi';
import { dealFinanceSummary, type Deal, type DealStats } from '../services/dealPricing';
import DealTagRail, { TagChip } from './deals/DealTagRail';
import DealCopyModal from './deals/DealCopyModal';
import { DealPriceCell, DealScenarioTiles } from './deals/DealPriceCell';
import {
  buildDealTree, effectiveDealTerms, filterTree, groupByTag, resolveDealTags,
  type DealFilter, type DealGroup, type DealNode, type StatusFilter,
} from './deals/dealsListModel';

/**
 * DEALS — THE LIST.
 *
 * Three things this page has to answer at a glance, none of which the old flat
 * table could:
 *
 *  1. WHAT DOES IT COST? A deal sells one offer several ways — buy the kit,
 *     finance it, rent it — and each way has a different monthly and a
 *     different amount due today. The row states the cheapest monthly, the
 *     least due today, and which option is cheapest overall, all from
 *     `dealScenarios` so the list and the editor quote the same figures.
 *
 *  2. WHAT ARE CUSTOMERS SHOWN? Landing-page exposure is a column, and opening
 *     a row renders the card a customer actually sees.
 *
 *  3. WHERE DID IT COME FROM? A platform runs one or two master deals and a
 *     great many variants of them. Variants nest under the deal they were
 *     copied from — as a DRAWING. Every deal in the tree is fully independent;
 *     nothing is inherited and no edit cascades.
 *
 * ⚠️ THE PRICES ON THIS PAGE COME FROM PUBLISHED VERSIONS, not from the deal
 * rows. A deal row is a working copy whose price may be an unpublished draft.
 * See `effectiveDealTerms`.
 */

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All deals' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'landing', label: 'On landing page' },
  { value: 'partner', label: 'Partner-only' },
];

/** How many variants of one family render before "load more". */
const FAMILY_PAGE = 25;
/** The batch endpoint's own cap — see `activeDealVersions`. */
const VERSION_BATCH = 100;

const DealsPage: React.FC<{ subheader?: React.ReactNode }> = ({ subheader }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [stats, setStats] = useState<DealStats | null>(null);
  const [tags, setTags] = useState<DealTag[]>([]);
  const [deviceSkus, setDeviceSkus] = useState<BillingSkuRow[]>([]);
  const [bundles, setBundles] = useState<DeviceBundleRow[]>([]);
  const [models, setModels] = useState<DeviceModelRow[]>([]);
  const [plansById, setPlansById] = useState<Record<string, { name?: string }>>({});

  /**
   * The active PUBLISHED version of each deal, filled in lazily for the rows
   * actually on screen. Never fetched for a family nobody has opened: a master
   * with a thousand variants would otherwise cost a thousand reads to paint a
   * screen showing one row.
   */
  const [versions, setVersions] = useState<Record<string, ActiveDealVersionSummary>>({});
  const [versionsLoading, setVersionsLoading] = useState(false);
  /** The row currently writing, so its switches disable rather than queue. */
  const [busyDealId, setBusyDealId] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [successTone, setSuccessTone] = useState<'success' | 'warn'>('success');

  const [search, setSearch] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [grouped, setGrouped] = useState(true);

  const [openFamilies, setOpenFamilies] = useState<Set<string>>(new Set());
  const [familyLimit, setFamilyLimit] = useState<Record<string, number>>({});
  const [openRows, setOpenRows] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const [copyTarget, setCopyTarget] = useState<Deal | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Deal | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // ── loading ───────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dealsRes, statsRes] = await Promise.all([
        api.deals.list(),
        api.deals.getStats(),
      ]);
      setDeals((dealsRes as any)?.deals || []);
      setStats((statsRes as any)?.stats || null);
      // Re-read every version: a deal's published state may have moved.
      setVersions({});

      /**
       * Everything below is best-effort and deliberately NOT awaited into the
       * page's loading state. A deals list that will not render because the
       * hardware catalog is briefly unreachable is worse than one that renders
       * without hardware prices for a moment.
       */
      api.deals.listTags()
        .then((r) => setTags((r.tags || []) as DealTag[]))
        .catch(() => setTags([]));
      billingApi.listCommissionPlans()
        .then((r) => setPlansById(Object.fromEntries((r.items || []).map((p: any) => [p.planId, p]))))
        .catch(() => setPlansById({}));
      Promise.all([billingApi.listSkus(), hardwareApi.listBundles(), hardwareApi.listModels()])
        .then(([skus, b, m]) => {
          setDeviceSkus((skus.items || []).filter((s) => s.type === 'device_finance'));
          setBundles(b.items || []);
          setModels(m.items || []);
        })
        .catch(() => { /* prices degrade to software-only; the list still works */ });
    } catch (err: any) {
      setError(err?.message || 'Failed to load deals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // A save on the editor navigates back here with a message in router state.
  useEffect(() => {
    const state = location.state as { dealSuccess?: string; dealSuccessTone?: 'success' | 'warn' } | null;
    if (state?.dealSuccess) {
      setSuccess(state.dealSuccess);
      setSuccessTone(state.dealSuccessTone || 'success');
      navigate(location.pathname, { replace: true, state: null });
      setTimeout(() => setSuccess(null), 4000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // ── the tree ──────────────────────────────────────────────────────────────

  const filter: DealFilter = useMemo(() => ({ search, tagIds, status }), [search, tagIds, status]);

  const tree = useMemo(() => buildDealTree(deals), [deals]);
  const filtered = useMemo(() => filterTree(tree, filter), [tree, filter]);
  const groups: DealGroup[] = useMemo(
    () => (grouped
      ? groupByTag(filtered.nodes, tags)
      : [{ key: '__all__', tag: null, nodes: filtered.nodes, dealCount: filtered.nodes.length, variantCount: 0 }]),
    [grouped, filtered.nodes, tags],
  );

  /**
   * A SEARCH OPENS THE FAMILIES IT MATCHED INSIDE.
   *
   * Without this, searching for a variant returns its master collapsed and the
   * hit stays hidden behind a "1,000 variants" chip — the list would answer
   * "somewhere in here", which is not an answer.
   */
  useEffect(() => {
    if (!search.trim()) return;
    setOpenFamilies(new Set(filtered.nodes.filter((n) => n.children.length > 0).map((n) => n.deal.id)));
  }, [search, filtered.nodes]);

  /** The rows actually rendered — the only deals whose versions are worth fetching. */
  const visibleNodes = useMemo(() => {
    const out: DealNode[] = [];
    const walkChildren = (node: DealNode) => {
      const limit = familyLimit[node.deal.id] ?? FAMILY_PAGE;
      node.children.slice(0, limit).forEach((child) => {
        out.push(child);
        if (openFamilies.has(child.deal.id)) walkChildren(child);
      });
    };
    for (const group of groups) {
      if (collapsedGroups.has(group.key)) continue;
      for (const root of group.nodes) {
        out.push(root);
        if (openFamilies.has(root.deal.id)) walkChildren(root);
      }
    }
    return out;
  }, [groups, collapsedGroups, openFamilies, familyLimit]);

  /**
   * Fetch the published version of every visible deal we do not already hold,
   * in batches. Runs again whenever opening a family reveals new rows.
   */
  useEffect(() => {
    const missing = Array.from(new Set(visibleNodes.map((n) => n.deal.id))).filter((id) => !(id in versions));
    if (missing.length === 0) return;

    let cancelled = false;
    setVersionsLoading(true);
    (async () => {
      try {
        for (let i = 0; i < missing.length; i += VERSION_BATCH) {
          const batch = missing.slice(i, i + VERSION_BATCH);
          const r = await dealsApi.activeVersions(batch);
          if (cancelled) return;
          setVersions((prev) => ({ ...prev, ...r.versions }));
        }
      } catch {
        /**
         * THE BATCH ENDPOINT MAY NOT BE THERE YET.
         *
         * `POST /deals/active-versions` ships with this page; a browser pointed
         * at a deals-service that predates it gets a 404, and without a
         * fallback every deal on the screen would read "unversioned" and quote
         * its row — the exact error this page exists to stop making.
         *
         * So fall back to the per-deal endpoint that has always existed. It is
         * one request per row, which is why it is not the primary path, and it
         * is bounded to the rows on screen. Silence here would be worse than
         * slowness: the numbers would be wrong rather than merely late.
         */
        const perDeal = await Promise.all(missing.slice(0, VERSION_BATCH).map(async (id) => {
          try {
            const r = await dealsApi.listVersions(id);
            const active = r.activeVersionNo === null
              ? null
              : (r.versions || []).find((v) => v.versionNo === r.activeVersionNo && v.status === 'published');
            const hasDraft = (r.versions || []).some((v) => v.status === 'draft');
            if (!active) return [id, { implicit: true, hasDraft, activeVersionNo: null }] as const;
            return [id, {
              implicit: false,
              hasDraft,
              activeVersionNo: active.versionNo,
              priceCents: active.priceCents,
              devicePackageIds: active.devicePackageIds ?? [],
              deviceTerms: (active.deviceTerms ?? {}) as Record<string, any>,
            }] as const;
          } catch {
            return null;
          }
        }));
        if (cancelled) return;
        const entries = perDeal.filter(Boolean) as [string, ActiveDealVersionSummary][];
        if (entries.length > 0) setVersions((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      } finally {
        if (!cancelled) setVersionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [visibleNodes, versions]);

  // ── per-deal pricing ──────────────────────────────────────────────────────

  /** A device SKU's display name — its bundle's or its model's, as the editor groups them. */
  const deviceNameFor = useCallback((sku: BillingSkuRow): string => {
    if (sku.bundleId) return bundles.find((b) => b.bundleId === sku.bundleId)?.name || sku.name;
    if (sku.modelId) return models.find((m) => m.modelId === sku.modelId)?.modelName || sku.name;
    return sku.name;
  }, [bundles, models]);

  const summaryFor = useCallback((deal: Deal) => {
    const terms = effectiveDealTerms(deal, versions[deal.id]);
    return {
      terms,
      summary: dealFinanceSummary({
        softwareMonthlyCents: terms.priceCents,
        devicePackageIds: terms.devicePackageIds,
        deviceTerms: terms.deviceTerms,
        deviceSkus,
        nameFor: deviceNameFor,
      }),
    };
  }, [versions, deviceSkus, deviceNameFor]);

  // ── writes ────────────────────────────────────────────────────────────────

  const handleToggleActive = async (deal: Deal) => {
    setBusyDealId(deal.id);
    try {
      await api.deals.update(deal.id, { isActive: deal.isActive !== 'true' });
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to toggle deal status');
    } finally {
      setBusyDealId(null);
    }
  };

  /**
   * TAKE IT DOWN FROM THE PRICING PAGE, from the list.
   *
   * This was only reachable by opening the deal and finding a checkbox in its
   * form, which is a long way to go to pull an offer that is live to the public
   * right now. `showOnLandingPage` is an OPERATIONAL field — super-admin-ext
   * writes it straight to the deal row rather than staging it as a draft
   * version — so taking an offer down takes effect immediately and needs no
   * publish. Nothing about the deal's money changes.
   */
  const handleToggleLanding = async (deal: Deal) => {
    setBusyDealId(deal.id);
    try {
      await api.deals.update(deal.id, { showOnLandingPage: !(deal as any).showOnLandingPage });
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to change where this deal is shown');
    } finally {
      setBusyDealId(null);
    }
  };

  const handleCopy = async (data: { name?: string; code?: string }) => {
    if (!copyTarget) return;
    const source = copyTarget;
    const res = await api.deals.duplicate(source.id, data);
    setCopyTarget(null);
    // Open the family so the new variant is visible where it landed, rather
    // than appearing to have gone nowhere.
    const rootId = (source as any).rootDealId || source.id;
    setOpenFamilies((prev) => new Set([...prev, source.id, rootId]));
    setSuccess(`Copied to “${res.deal?.name}” (${res.deal?.code}) — inactive and hidden until you publish it.`);
    setSuccessTone('warn');
    await fetchData();
    setTimeout(() => setSuccess(null), 6000);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.isActive === 'true') {
      setError('Cannot delete an active deal. Please deactivate it first.');
      setDeleteTarget(null);
      return;
    }
    setActionLoading('delete');
    try {
      await api.deals.delete(deleteTarget.id);
      setSuccess('Deal deleted successfully');
      setSuccessTone('success');
      setDeleteTarget(null);
      await fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.message || 'Failed to delete deal');
    } finally {
      setActionLoading(null);
    }
  };

  const saveTags = async (next: DealTag[]) => {
    const res = await api.deals.saveTags(next);
    setTags((res.tags || next) as DealTag[]);
  };

  const toggle = (set: Set<string>, id: string) => {
    const n = new Set(set);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  };

  // ── header ────────────────────────────────────────────────────────────────

  const variantTotal = useMemo(() => deals.filter((d) => (d as any).parentDealId).length, [deals]);
  const landingTotal = useMemo(() => deals.filter((d) => (d as any).showOnLandingPage).length, [deals]);

  const header = (
    <div className="bg-black">
      <div className="flex items-center justify-between gap-4 px-4 py-3 md:px-6">
        <div className="flex flex-shrink-0 items-center gap-3">
          <Tag size={24} className="text-white" />
          <div>
            <h1 className="text-base font-semibold text-white md:text-lg">Deals</h1>
            <p className="text-[11px] text-gray-400">What a merchant buys — software, hardware, and the version they sign</p>
          </div>
        </div>

        <div className="hidden items-center gap-6 lg:flex">
          <div className="text-center">
            <div className="font-mono text-xl font-bold tabular-nums text-white">{stats?.totalDeals ?? deals.length}</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Total</div>
          </div>
          <div className="text-center">
            <div className="font-mono text-xl font-bold tabular-nums text-emerald-400">{stats?.activeDeals ?? 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Active</div>
          </div>
          <div className="text-center">
            <div className="font-mono text-xl font-bold tabular-nums text-sky-400">{landingTotal}</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">On landing</div>
          </div>
          <div className="text-center">
            <div className="font-mono text-xl font-bold tabular-nums text-rose-400">{variantTotal}</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Variants</div>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          <button
            onClick={fetchData}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-white/10 hover:text-white"
            title="Refresh"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
          <button
            onClick={() => navigate('/deals/new')}
            className="flex min-h-[44px] items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-rose-700"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Create Deal</span>
          </button>
        </div>
      </div>
      {subheader && <div className="px-4 pb-2 md:px-6">{subheader}</div>}
      <div style={{ background: 'linear-gradient(90deg, #e8365a, #DC143C, #9F1239)', height: '3px' }} />
    </div>
  );

  if (loading) {
    return (
      <div className="-m-3 min-h-screen bg-black md:-m-6">
        {header}
        <RubyLoader variant="page" className="min-h-[calc(100vh-72px)] bg-stone-100" />
      </div>
    );
  }

  // ── one deal row ──────────────────────────────────────────────────────────

  const renderRow = (node: DealNode, isLastChild: boolean) => {
    const deal = node.deal;
    const { terms, summary } = summaryFor(deal);
    const dealTags = resolveDealTags(deal, tags);
    const isOpen = openRows.has(deal.id);
    const familyOpen = openFamilies.has(deal.id);
    const isContext = filtered.contextIds.has(deal.id);
    const indent = node.depth * 28;

    return (
      <div key={deal.id} className={`border-b border-stone-100 last:border-b-0 ${deal.isActive !== 'true' ? 'opacity-60' : ''}`}>
        <div
          className={`relative grid cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-stone-50
                      md:grid-cols-[minmax(280px,2.1fr)_1.5fr_1.2fr_0.8fr_0.9fr] ${isContext ? 'bg-stone-50/60' : ''}`}
          style={{ paddingLeft: 16 + indent }}
          onClick={() => setOpenRows((p) => toggle(p, deal.id))}
        >
          {/* the elbow connecting a variant to the deal it was copied from */}
          {node.depth > 0 && (
            <>
              <span
                aria-hidden
                className="pointer-events-none absolute w-px bg-stone-200"
                style={{ left: 16 + indent - 17, top: 0, bottom: isLastChild ? '50%' : 0 }}
              />
              <span
                aria-hidden
                className="pointer-events-none absolute h-px w-[13px] bg-stone-200"
                style={{ left: 16 + indent - 17, top: '50%' }}
              />
            </>
          )}

          {/* identity */}
          <div className="flex min-w-0 items-center gap-2.5">
            <ChevronRight
              className={`h-3.5 w-3.5 shrink-0 text-stone-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
            />
            {node.depth === 0 && (
              <div className="shrink-0 rounded-lg border border-rose-100 bg-rose-50 p-2">
                <Tag size={16} className="text-rose-600" />
              </div>
            )}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[13.5px] font-semibold text-stone-800">{deal.name}</span>
                {node.depth === 0 && node.descendantCount > 0 && (
                  <span className="rounded-full bg-stone-800 px-1.5 py-[1.5px] text-[10px] font-bold text-white">MASTER</span>
                )}
                {node.depth > 0 && (
                  <span className="rounded-full border border-stone-200 bg-stone-100 px-1.5 py-[1.5px] text-[10px] font-bold text-stone-500">
                    VARIANT
                  </span>
                )}
                {deal.isDefaultDeal && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-[1.5px] text-[10px] font-bold text-amber-700">
                    <Star className="h-2.5 w-2.5" /> Default
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <code className="rounded bg-stone-100 px-1.5 py-0.5 text-[10.5px] text-stone-500">{deal.code}</code>
                {dealTags.map((t) => <TagChip key={t.id} tag={t} />)}
              </div>
            </div>
          </div>

          {/* auto-priced */}
          <div>
            <DealPriceCell summary={summary} softwareMonthlyCents={terms.priceCents} />
          </div>

          {/*
            LIVE STATE, AND THE SWITCHES FOR IT.

            Both of these were read-only here. "Public/Partner-only" was a badge
            with no control at all, and the only way to stop selling a deal was a
            small eye icon among four identical grey icons in the actions column
            — which reads as "preview", not "active". Pulling an offer that is
            live to the public meant opening the deal and hunting for a checkbox
            in its form.

            They are two DIFFERENT questions and both are asked here now:
              • On the pricing page — is a stranger offered this?
              • Selling — can it be redeemed at all, by anyone, including by
                code from a rep?
            An offer taken off the page still works for anyone holding its code;
            a paused offer works for nobody. Neither touches the deal's money,
            so neither needs a version published.
          */}
          <div className="min-w-0" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                disabled={busyDealId === deal.id}
                onClick={() => handleToggleLanding(deal)}
                title={(deal as any).showOnLandingPage
                  ? 'Showing on the pricing page — click to take it down'
                  : 'Not on the pricing page — click to publish it there'}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[10px] font-bold transition-colors disabled:opacity-50 ${
                  (deal as any).showOnLandingPage
                    ? 'bg-sky-100 text-sky-800 hover:bg-sky-200'
                    : 'border border-stone-200 bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                <Globe className="h-2.5 w-2.5" />
                {(deal as any).showOnLandingPage ? 'Public' : 'Partner-only'}
              </button>

              <button
                type="button"
                disabled={busyDealId === deal.id}
                onClick={() => handleToggleActive(deal)}
                title={deal.isActive === 'true'
                  ? 'Selling — click to pause it everywhere, including by code'
                  : 'Paused — click to start selling it again'}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[10px] font-bold transition-colors disabled:opacity-50 ${
                  deal.isActive === 'true'
                    ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                    : 'border border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${deal.isActive === 'true' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                {deal.isActive === 'true' ? 'Selling' : 'Paused'}
              </button>
            </div>
            {(deal as any).landingPageLabel && (
              <div className="mt-1 truncate text-[11px] leading-snug text-stone-500">“{(deal as any).landingPageLabel}”</div>
            )}
          </div>

          {/* version — WHICH price the row above is quoting */}
          <div className="flex flex-wrap items-center gap-1">
            {terms.source === 'published' ? (
              <span className="rounded-full border border-emerald-200 bg-emerald-100 px-2 py-[1.5px] font-mono text-[10.5px] font-bold text-emerald-700">
                v{terms.activeVersionNo} live
              </span>
            ) : (
              <span
                className="rounded-full border border-stone-200 bg-stone-100 px-2 py-[1.5px] text-[10.5px] font-semibold text-stone-600"
                title="Never published — this row quotes the deal itself as its implicit v1"
              >
                unversioned
              </span>
            )}
            {terms.hasDraft && (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-[1.5px] text-[10.5px] font-semibold text-amber-800">
                draft
              </span>
            )}
          </div>

          {/* actions */}
          <div className="flex items-center justify-end gap-0.5 pr-1" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setCopyTarget(deal)}
              title="Copy into a variant"
              className="rounded p-1.5 text-stone-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
            >
              <Copy className="h-[15px] w-[15px]" />
            </button>
            <button
              onClick={() => navigate(`/deals/${deal.id}`)}
              title="Edit"
              className="rounded p-1.5 text-stone-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
            >
              <Edit2 className="h-[15px] w-[15px]" />
            </button>
            <button
              onClick={() => setDeleteTarget(deal)}
              title={deal.isActive === 'true' ? 'Deactivate first' : 'Delete'}
              className="rounded p-1.5 text-stone-400 transition-colors hover:bg-red-50 hover:text-red-600"
            >
              <Trash2 className="h-[15px] w-[15px]" />
            </button>
          </div>
        </div>

        {/* ── expanded: what the customer sees, and every way to pay ───────── */}
        {isOpen && (
          <div
            className="grid gap-4 border-t border-stone-200 bg-stone-50 px-4 py-4 lg:grid-cols-[360px_1fr]"
            style={{ paddingLeft: 16 + indent }}
          >
            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">What the customer sees</span>
                {!(deal as any).showOnLandingPage && (
                  <span className="rounded-full bg-stone-200 px-1.5 py-[1.5px] text-[9.5px] font-bold text-stone-600">NOT PUBLISHED TO THE SITE</span>
                )}
              </div>
              <div className={`rounded-2xl border-2 bg-white p-5 shadow-sm ${
                (deal as any).showOnLandingPage ? 'border-sky-200' : 'border-stone-200'
              }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[15px] font-bold text-stone-900">
                      {(deal as any).landingPageLabel || deal.name}
                    </div>
                    {(deal as any).landingPageDescription && (
                      <div className="mt-0.5 text-[11.5px] leading-snug text-stone-500">
                        {(deal as any).landingPageDescription}
                      </div>
                    )}
                  </div>
                  {deal.isDefaultDeal && (
                    <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-[1.5px] text-[10px] font-bold text-amber-700">★ Popular</span>
                  )}
                </div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-mono text-3xl font-extrabold tabular-nums text-stone-900">
                    {money(summary.minMonthly ? summary.minMonthly.monthlyCents : terms.priceCents)}
                  </span>
                  <span className="text-[12px] text-stone-500">/mo</span>
                </div>
                {summary.minDown && (
                  <div className="mt-0.5 text-[11.5px] font-semibold text-emerald-700">
                    {summary.minDown.visibleDueTodayCents === 0
                      ? '$0 due today'
                      : `from ${money(summary.minDown.visibleDueTodayCents)} due today`}
                  </div>
                )}
                <div className="mt-3 font-mono text-[10.5px] text-stone-400">code {deal.code}</div>
              </div>
              <p className="mt-2 text-[10.5px] leading-snug text-stone-400">
                Built from the deal’s landing label and description and the auto-picked quote. The live card is
                rendered by the landing page itself — open the deal to preview it exactly.
              </p>
            </div>

            <div>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Price the whole kit as</span>
                <span className="text-[10.5px] text-stone-400">
                  {terms.devicePackageIds.length} device offer{terms.devicePackageIds.length === 1 ? '' : 's'}
                  {terms.source === 'published'
                    ? ` · frozen in v${terms.activeVersionNo}`
                    : ' · from the deal row (never published)'}
                </span>
              </div>
              <DealScenarioTiles summary={summary} />
            </div>
          </div>
        )}

        {/* ── the family ───────────────────────────────────────────────────── */}
        {node.children.length > 0 && (
          <div className="border-t border-stone-100 bg-white">
            <button
              onClick={() => setOpenFamilies((p) => toggle(p, deal.id))}
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left transition-colors hover:bg-stone-50"
              style={{ paddingLeft: 34 + indent }}
            >
              <ChevronRight className={`h-3 w-3 text-stone-400 transition-transform ${familyOpen ? 'rotate-90' : ''}`} />
              <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-[1.5px] text-[10px] font-bold text-rose-700">
                <span className="font-mono tabular-nums">{node.descendantCount.toLocaleString()}</span> variant{node.descendantCount === 1 ? '' : 's'}
              </span>
              <span className="text-[11px] text-stone-400">copied from this deal · each fully independent</span>
            </button>

            {familyOpen && (() => {
              const limit = familyLimit[deal.id] ?? FAMILY_PAGE;
              const shown = node.children.slice(0, limit);
              return (
                <>
                  {shown.map((child, i) => renderRow(child, i === shown.length - 1))}
                  {node.children.length > limit && (
                    <button
                      onClick={() => setFamilyLimit((p) => ({ ...p, [deal.id]: limit + FAMILY_PAGE }))}
                      className="m-3 w-[calc(100%-1.5rem)] rounded-lg border border-dashed border-stone-300 py-2 text-[12px]
                                 font-semibold text-stone-500 transition hover:border-rose-400 hover:text-rose-600"
                    >
                      Load {Math.min(FAMILY_PAGE, node.children.length - limit)} more
                      {' '}of {node.children.length.toLocaleString()}
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  // ── page ──────────────────────────────────────────────────────────────────

  return (
    <div className="-m-3 min-h-screen bg-black md:-m-6">
      {header}

      <div className="min-h-[calc(100vh-72px)] bg-stone-100 px-4 py-4 md:px-6 md:py-6">
        <div className="mx-auto max-w-[1400px] space-y-4">

          {error && (
            <div className="flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="shrink-0 font-medium text-red-600 hover:text-red-800">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          {success && (
            <div className={`flex items-center gap-2 rounded-xl px-4 py-3 ${
              successTone === 'warn'
                ? 'border border-amber-200 bg-amber-50 text-amber-900'
                : 'border border-emerald-200 bg-emerald-50 text-emerald-700'
            }`}
            >
              {successTone === 'warn' ? <AlertTriangle className="h-4 w-4 shrink-0" /> : <Check className="h-4 w-4 shrink-0" />}
              <span>{success}</span>
            </div>
          )}

          {/* toolbar */}
          <div className="rounded-xl border border-stone-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center gap-3 border-b border-stone-100 p-3 md:p-4">
              <div className="relative min-w-[220px] flex-1">
                <Search className="absolute left-3 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-stone-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search deals, codes, variants…"
                  className="w-full rounded-lg border border-stone-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                />
              </div>

              <div className="flex items-center gap-1 rounded-lg bg-stone-100 p-0.5">
                <span className="px-2.5 py-1.5 text-[11.5px] font-semibold text-stone-500">Group</span>
                <button
                  onClick={() => setGrouped(true)}
                  className={`rounded-md px-3 py-1.5 text-[11.5px] font-semibold transition ${
                    grouped ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                  }`}
                >
                  Tag
                </button>
                <button
                  onClick={() => setGrouped(false)}
                  className={`rounded-md px-3 py-1.5 text-[11.5px] font-semibold transition ${
                    !grouped ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                  }`}
                >
                  None
                </button>
              </div>

              {/* Never a native <select> — it renders as an OS control inside
                  the iOS webview and cannot be themed. */}
              <div className="w-full sm:w-52">
                <SearchableDropdown
                  options={STATUS_OPTIONS}
                  value={status}
                  onChange={(v) => setStatus((v as StatusFilter) || 'all')}
                  variant="light"
                />
              </div>

              {versionsLoading && (
                <span className="inline-flex items-center gap-1.5 text-[11px] text-stone-400">
                  <RubyGemMark size={12} drawing /> reading published versions
                </span>
              )}
            </div>

            <DealTagRail
              tags={tags}
              selectedIds={tagIds}
              onToggle={(id) => setTagIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))}
              onClear={() => setTagIds([])}
              onSave={saveTags}
            />
          </div>

          {/* groups */}
          {groups.map((group) => {
            const collapsed = collapsedGroups.has(group.key);
            return (
              <div key={group.key} className={`overflow-hidden rounded-xl border bg-white shadow-sm ${
                group.tag === null && grouped ? 'border-dashed border-stone-300' : 'border-stone-200'
              }`}
              >
                {grouped && (
                  <button
                    onClick={() => setCollapsedGroups((p) => toggle(p, group.key))}
                    className="flex w-full items-center gap-3 border-b border-stone-200 bg-stone-50 px-4 py-3 text-left"
                  >
                    <ChevronRight className={`h-3.5 w-3.5 text-stone-400 transition-transform ${collapsed ? '' : 'rotate-90'}`} />
                    {group.tag
                      ? <TagChip tag={group.tag} size="md" />
                      : <span className="text-[12px] font-semibold text-stone-500">Untagged</span>}
                    <span className="text-[12px] text-stone-500">
                      <span className="font-mono tabular-nums">{group.dealCount}</span> deal{group.dealCount === 1 ? '' : 's'}
                      {group.variantCount > 0 && <> · <span className="font-mono tabular-nums">{group.variantCount.toLocaleString()}</span> variants</>}
                    </span>
                  </button>
                )}

                {!collapsed && (
                  <>
                    <div className="hidden grid-cols-[minmax(280px,2.1fr)_1.5fr_1.2fr_0.8fr_0.9fr] gap-3 border-b border-stone-100 bg-stone-50/60 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-stone-400 md:grid">
                      <div>Deal</div>
                      <div>Auto-priced from the kit</div>
                      <div>Where it is live</div>
                      <div>Version</div>
                      <div className="pr-1 text-right">Actions</div>
                    </div>
                    {group.nodes.map((n, i) => renderRow(n, i === group.nodes.length - 1))}
                  </>
                )}
              </div>
            );
          })}

          {groups.every((g) => g.nodes.length === 0) && (
            <div className="rounded-xl border border-stone-200 bg-white py-12 text-center text-stone-400 shadow-sm">
              {deals.length === 0
                ? 'No deals found. Create your first deal to get started.'
                : 'No deals match these filters.'}
            </div>
          )}
        </div>
      </div>

      {copyTarget && (
        <DealCopyModal
          deal={copyTarget}
          onCancel={() => setCopyTarget(null)}
          onConfirm={handleCopy}
        />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-stone-200 bg-white p-6 text-stone-800 shadow-2xl">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-full bg-red-100 p-2"><AlertCircle className="h-5 w-5 text-red-600" /></div>
              <h2 className="text-lg font-bold text-stone-800">Delete Deal</h2>
            </div>

            {deleteTarget.isActive === 'true' ? (
              <p className="mb-4 text-stone-600">
                <strong>{deleteTarget.name}</strong> is currently active.
                <span className="mt-2 block text-sm text-red-600">Please deactivate the deal first before deleting it.</span>
              </p>
            ) : (
              <p className="mb-4 text-stone-600">
                Are you sure you want to permanently delete <strong>{deleteTarget.name}</strong>? This action cannot be undone.
                {deleteTarget.affiliateCount > 0 && (
                  <span className="mt-2 block text-sm text-amber-600">
                    This deal is assigned to {deleteTarget.affiliateCount} affiliate(s). They will be unassigned.
                  </span>
                )}
                {/* Variants are independent deals and are NOT deleted with their
                    master — but the tree they hung from is about to disappear,
                    which is a surprise worth naming before it happens. */}
                {deals.some((d) => (d as any).parentDealId === deleteTarget.id) && (
                  <span className="mt-2 block text-sm text-amber-600">
                    Its variants are independent deals and will NOT be deleted — they become top-level deals.
                  </span>
                )}
              </p>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="min-h-[44px] flex-1 rounded-xl bg-stone-100 px-4 py-2.5 text-sm font-medium text-stone-700 transition hover:bg-stone-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading === 'delete' || deleteTarget.isActive === 'true'}
                className="min-h-[44px] flex-1 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading === 'delete' ? <span className="flex justify-center"><RubyGemMark size={20} drawing color="currentColor" /></span> : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealsPage;
